import { postVaultProxyLogsByLogIdRevealMutation } from '@onefootprint/fetch-dashboard';
import { IcoDotSmall16 } from '@onefootprint/icons';
import type { TraceProxyRequestLog } from '@onefootprint/request-types/dashboard';
import { Badge, LinkButton, Tag } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePermissions from '@/hooks/use-permissions';
import LogItemDetails from '../log-item-details';
import TimelineItemTime from './components/timeline-item-time';
type LogTimelineItemProps = {
  log: TraceProxyRequestLog;
  isLast: boolean;
  defaultShowDetails?: boolean;
};
const LogTimelineItem = ({
  log,
  isLast,
  defaultShowDetails = false
}: LogTimelineItemProps) => {
  const {
    t
  } = useTranslation('vault-proxy', {
    keyPrefix: 'details.logs'
  });
  const [isShowingDetails, setIsShowingDetails] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const {
    hasPermission
  } = usePermissions();
  const revealLogDetailsMutation = useMutation(postVaultProxyLogsByLogIdRevealMutation());
  const {
    proxyRequestLogId: logId,
    sentAt,
    statusCode,
    method
  } = log;
  const isErrorCode = statusCode && statusCode >= 400;
  useEffect(() => {
    if (defaultShowDetails && !isShowingDetails && !revealLogDetailsMutation.data) {
      handleShowDetails();
    }
  }, [defaultShowDetails]);
  const handleShowDetails = async () => {
    const hasManageVaultProxyPermission = hasPermission('manage_vault_proxy');
    if (hasManageVaultProxyPermission) {
      try {
        await revealLogDetailsMutation.mutateAsync({
          path: {
            logId
          }
        });
        setIsShowingDetails(true); // Show the log details
      } catch (_error) {}
    } else {
      setIsShowingDetails(true); // Show the no permission message
    }
  };
  const handleHideDetails = () => {
    setIsShowingDetails(false);
  };
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);
  return <div className="relative py-2 last:pb-0">
      {!isLast && <div className={cx('absolute top-[34px] left-[7px] w-0.5 bg-[var(--border-color-primary)]', {
      'h-[calc(100%-20px)]': isShowingDetails,
      'h-7': !isShowingDetails
    })} />}
      <div className="relative flex items-center min-h-[32px]">
        <div className="w-4 h-4 flex items-center justify-center mr-4 relative">
          <IcoDotSmall16 />
        </div>
        {/* Touch has no hover, so on a phone the details link always shows and wraps under the row. */}
        <div className="flex-1 min-w-0 relative flex flex-wrap items-center gap-x-2 gap-y-1 md:min-w-auto md:flex-nowrap md:gap-2" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <div className="grid grid-cols-[135px_40px_1fr] gap-3 items-center relative" style={{
          zIndex: 1
        }}>
            <TimelineItemTime timestamp={sentAt} />
            <Badge variant={isErrorCode ? 'error' : 'success'}>{statusCode || '-'}</Badge>
            <div className="flex items-center">
              <Tag>{method}</Tag>
            </div>
          </div>
          <span className={cx('hidden text-snippet-2 text-tertiary transition-opacity md:inline', {
          'opacity-100': isHovered || isShowingDetails,
          'opacity-0': !isHovered && !isShowingDetails
        })}>
            ⋅
          </span>
          <LinkButton variant="caption-1" onClick={isShowingDetails ? handleHideDetails : handleShowDetails} className={cx('transition-opacity', {
          'opacity-100': isHovered || isShowingDetails,
          'opacity-100 md:opacity-0': !isHovered && !isShowingDetails
        })}>
            {isShowingDetails ? t($ => $.showLess) : t($_0 => $_0.moreDetails)}
          </LinkButton>
        </div>
      </div>
      {revealLogDetailsMutation.data && isShowingDetails && <LogItemDetails data={revealLogDetailsMutation.data} />}
      {!revealLogDetailsMutation.data && isShowingDetails && <div className="ml-10 mt-2">
          <span className="text-body-2 text-tertiary">{t($_1 => $_1.noPermission)}</span>
        </div>}
    </div>;
};
export default LogTimelineItem;